/*
{
  "topic": {
    "id": 77,
    "name": "Basics Bundle"
  },
  "game_state": {
    "level": "A1",
    "words": [
      "牛乳", "に", "砂糖", "を", "入れます", "。"
    ],
    "blanks": [
      true, false, false, false, false, false
    ],
    "options": [
      "コーヒー", "クロワッサン", "お願いします", "牛乳"
    ],
    "game_type": "sentence-builder", // 'sentence-builder-audio', 'sentence-builder'
    "concept_ids": [
      1222, 1470
    ],
    "distractors": [
      "コーヒー", "クロワッサン", "お願いします"
    ],
    "correct_sentence": "牛乳に砂糖を入れます。",
    "reference_sentence": "I put sugar in the milk."
  }
}
*/
<template>
  <div
    class="game-container border p-3 shadow rounded-4 mt-2"
    :class="{ compact: compact }"
    v-if="!loading"
  >
     <!-- AUDIO-MODE CONTROLS -->
     <div v-if="isAudioMode" class="audio-controls mb-2 text-center" :class="{ compact: compact }">
       <button type="button"
               class="btn btn-outline-primary btn-outline-primary-cs me-2"
               @click="playAudio(false)">
         <i class="bi bi-volume-up-fill me-2"></i>{{ translations.playAgain }}
       </button>
       <button type="button"
               class="btn btn-outline-primary btn-outline-primary-cs"
               @click="playAudio(true)">
         <i class="bi bi-volume-down-fill me-2"></i>{{ translations.playSlow }}
       </button>
     </div>

    <!-- Show translation only in TEXT mode -->
    <div v-if="!isAudioMode" class="reference-section" :class="{ compact: compact }">
      <p class="reference-sentence" :class="{ compact: compact }">{{ question.reference_sentence }}</p>
    </div>

    <div class="word-bank" :class="{ compact: compact }">
      <div class="words-container" :class="{ compact: compact }">
        <div 
          v-for="word in availableWords" 
          :key="word.id"
          :class="['word-item', { selected: word.selected, compact: compact }]"
          @click="addWordToBlank(word)"
          v-html="word.text"
        >
        </div>
      </div>
    </div>
    <div v-if="debug">
      correct answer: <span class="text-success" v-html="question.correct_sentence"></span><br>
      RTL mode: <span :class="isRTL ? 'text-primary' : 'text-secondary'">{{ isRTL ? 'YES' : 'NO' }}</span><br>
      learning language: <span class="text-info">{{ profileStore.profile?.learning_language || 'not set' }}</span><br>
      order normal: <span class="text-muted" v-html="correctOrder.map(t => t.text).join(' | ')"></span><br>
      order RTL: <span class="text-muted" v-html="isRTL ? correctOrder.slice().reverse().map(t => t.text).join(' | ') : 'N/A'"></span>
    </div>

    <div class="sentence-builder" :class="{ compact: compact }">
      <h4 :class="{ compact: compact }">{{ translations.yourSentence }}</h4>
      <div class="drop-zone" :class="{ compact: compact, rtl: isRTL }">
        <div 
          v-for="(slot, index) in (isRTL ? correctOrder.slice().reverse() : correctOrder)" 
          :key="index"
          class="word-slot"
        >
          <div 
            v-if="userSentenceFill[isRTL ? correctOrder.length - 1 - index : index] && userSentenceFill[isRTL ? correctOrder.length - 1 - index : index].fixed"
            class="preset-text"
            :class="{ compact: compact }"
            v-html="userSentenceFill[isRTL ? correctOrder.length - 1 - index : index].text"
          >
          </div>
          <div 
            v-else-if="userSentenceFill[isRTL ? correctOrder.length - 1 - index : index]"
            class="word-item placed"
            :class="{ compact: compact }"
            @click="removeWordFromBlank(isRTL ? correctOrder.length - 1 - index : index)"
            v-html="userSentenceFill[isRTL ? correctOrder.length - 1 - index : index].text"
          >
          </div>
          <div v-else class="placeholder" :class="{ compact: compact }">[ ]</div>
        </div>
      </div>
    </div>
    
    <div class="actions" :class="{ compact: compact }" v-if="!feedback">
      <button @click="clearSentence" type="button" class="btn btn-outline-secondary btn-regular-padding">{{ translations.clear }}</button>
      <button @click="checkAnswer" type="button" :disabled="!canCheckAnswer" class="btn btn-primary btn-primary-cs-nw btn-primary-sm" :class="{ compact: compact }">{{ translations.checkAnswer }}</button>
    </div>

    <div v-if="feedback && !isCorrect" class="feedback" :class="{ correct: isCorrect, incorrect: !isCorrect, compact: compact }">
      <p v-if="!isCorrect" v-html="question.correct_sentence" />
      <p v-if="gameState.game_type === 'sentence-builder-audio'">
        <strong>{{ gameState.reference_sentence }}</strong>
      </p>
    </div>
    <div v-else-if="feedback && isCorrect && gameState.game_type === 'sentence-builder-audio'" class="feedback correct" :class="{ compact: compact }">
      <p><strong v-html="question.correct_sentence" /></p>
      <p><strong>{{ question.reference_sentence }}</strong></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, inject } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useProfileStore } from '@/stores/profile.store'
import { useI18n } from 'vue-i18n'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
const { t } = useI18n()
const debug = ref(false)

// Define the prop and emitted event
const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  // currently just for use in the registration game, we make the buttons smaller like mobile
  compact: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['next-question'])

const translations = {
  yourSentence:   t("curriculum.your-sentence"),
  clear:          t("curriculum.clear"),
  checkAnswer:    t("game.check-answer"),
  correct:        t("curriculum.correct1"),
  incorrect:      t("curriculum.incorrect"),
  correctAnswer:  t("game.the-correct-answer-is"),
  playAgain:     t("curriculum.play-again"),
  playSlow:      t("curriculum.play-slow"),
}

const audiomanager  = inject('audiomanager')
const { user } = useAuthStore()
const profileStore = useProfileStore()

// Reactive state variables
const question = ref<any>(null)
const correctOrder = ref<any[]>([])
const availableWords = ref<any[]>([])
const userSentenceFill = ref<any[]>([])
const loading = ref(true)
const error = ref('')
const feedback = ref('')
const isCorrect = ref(false)

// --- NEW: recognise audio mode ---
const isAudioMode = computed(() => props.gameState.game_type === 'sentence-builder-audio')

// --- NEW: recognise RTL languages ---
const isRTL = computed(() => {
  const rtlLanguages = ['Persian', 'Urdu', 'Arabic']
  return rtlLanguages.includes(profileStore.profile.learning_language)
})

// Utility function for shuffling arrays
const shuffleArray = (array) => {
  const arr = array.slice()
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

// Initialize game state based on the provided prop.
// Expects props.gameState to include a "gameType" property
// ("full" or "fill") along with the required data.
onMounted(() => {
  console.log('init game state', props.gameState)
  // Reset error and feedback
  error.value = ''
  feedback.value = ''

  question.value = {
    unannotated_correct_sentence: props.gameState.words.join(' '),
    correct_sentence: props.gameState.annotated_words.join(' '),
    reference_sentence: props.gameState.reference_sentence,
  }

  // --- changed: use blanks instead of presets (inverse meaning) ---
  correctOrder.value = props.gameState.annotated_words.map((text, idx) => ({
    id: idx,
    text,
    blank: props.gameState.blanks[idx],   // true → user must fill
  }))

  // slot is null when blank === true, otherwise fixed
  userSentenceFill.value = correctOrder.value.map(token =>
    token.blank ? null : { ...token, fixed: true }
  )

  // words already shown in the sentence (blank === false) must be removed from options
  const fixedValues = correctOrder.value
    .filter(t => !t.blank)
    .map(t => t.text)

  availableWords.value = shuffleArray(
    props.gameState.annotated_options
      .filter(opt => !fixedValues.includes(opt))
      .map((text, idx) => ({ id: idx, text, selected: false }))
  )
  loading.value = false

  // Auto-play once in audio mode
  if (isAudioMode.value) playAudio()
})

// --- AUDIO helpers reused by both modes ---
function playAudio(slow = false) {
  if (!audiomanager) return
  const lang = profileStore.profile.learning_language
  const text = question.value.unannotated_correct_sentence
  if (!lang || !text) return
  const url = getBaseUrl(`/tts/?lang=${lang}&text=${encodeURIComponent(text)}&token=${user.token}`)
  audiomanager.setPlaybackSpeed(slow ? 0.5 : 1)
  audiomanager.play(url)
}

const addWordToBlank = (word) => {
  if (word.selected) return
  const index = userSentenceFill.value.findIndex(slot => slot === null)
  if (index !== -1) {
    userSentenceFill.value[index] = {
      id: Date.now() + Math.random(),
      text: word.text,
      originalId: word.id,
      fixed: false
    }
    const target = availableWords.value.find(w => w.id === word.id)
    if (target) target.selected = true
  }
}

const removeWordFromBlank = (index) => {
  const slot = userSentenceFill.value[index]
  if (!slot) return
  const target = availableWords.value.find(w => w.id === slot.originalId)
  if (target) target.selected = false
  if (!slot.fixed) {
    userSentenceFill.value[index] = null
  }
}

const clearSentence = () => {
  userSentenceFill.value = userSentenceFill.value.map(slot => (slot && slot.fixed ? slot : null))
  availableWords.value.forEach(word => (word.selected = false))
}

const canCheckAnswer = computed(() => {
  return userSentenceFill.value.every(slot => slot !== null)
})

const checkAnswer = async () => {
  const userTokens = userSentenceFill.value.map(slot => (slot ? slot.text : ''))
  const correctTokens = correctOrder.value.map(token => token.text)
  isCorrect.value = userTokens.every((w, i) => w === correctTokens[i])
  feedback.value = isCorrect.value ? 'Great job!' : 'Not quite right.'
  const result = await emit('game-action', {
    selected: userTokens,
    expected: correctTokens,
    correct: isCorrect.value,
  })
}

</script>

<style scoped>
.debug-panel {
  margin-bottom: 15px;
  text-align: center;
}
.debug-panel label {
  margin: 0 10px;
  font-weight: bold;
}
.language-toggle {
  font-weight: bold;
}
.loading, .error {
  text-align: center;
  margin: 20px 0;
}
.game-container {
  max-width: 800px;
  margin: 0 auto;
}
.reference-section {
  text-align: center;
  margin: 0;
  padding: 5px;
  background-color: #f8f9fa;
  border-radius: 10px;
}
.reference-sentence {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin: 0;
}
.instruction {
  color: #666;
  font-style: italic;
}
.word-bank, .sentence-builder {
  margin: 10px 0;
}
.words-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  min-height: 60px;
  padding: 15px;
  border: 2px dashed #ddd;
  border-radius: 5px;
}
.word-item {
  /* background-color: #007bff; */
	background-color: var(--primary-color);
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
  min-width: 40px;
  text-align: center;
}
.ion-color-primary {
	background-color: var(--primary-color);
  --ion-color-base: var(--primary-color) !important;
}
.word-item:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}
/* New style for selected words: gray background and transparent text but keeping same size */
.word-item.selected {
  background-color: #cccccc;
  color: transparent;
}
.drop-zone {
  min-height: 60px;
  padding: 15px;
  border: 2px dashed #28a745;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
}
.drop-zone.rtl {
  direction: rtl;
  justify-content: center;
  flex-direction: row-reverse;
}
.drop-zone.rtl .word-slot {
  direction: rtl;
}
.drop-zone.rtl .preset-text,
.drop-zone.rtl .word-item.placed,
.drop-zone.rtl .placeholder {
  direction: rtl;
  text-align: right;
}
.word-slot {
  width: auto;
}
.word-item.placed {
  background-color: #28a745;
}
.word-item.placed.fixed {
  cursor: default;
}
.word-item.placed:hover {
  background-color: #1e7e34;
}
.placeholder {
  color: #999;
  font-style: italic;
  padding: 8px 15px;
  border: 1px dashed #ccc;
  border-radius: 20px;
  min-width: 40px;
  text-align: center;
}
.actions {
  text-align: center;
  margin: 30px 0;
}
.actions button {
  margin: 0 10px;
}
.feedback {
  margin: 5px 0;
  padding: 5px;
  border-radius: 10px;
  text-align: center;
}
.feedback.correct {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}
.feedback.incorrect {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}
.feedback h4 {
  margin-bottom: 5px;
}
.preset-text {
  font-size: 16px;
  padding: 8px;
  color: #333;
}
.sentence-builder-game {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}
.pool {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin: 10px 0;
}
.pool button {
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}
.pool button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.pool button:hover:not(:disabled) {
  background-color: #0056b3;
}
.drop-zone {
  min-height: 60px;
  padding: 15px;
  border: 2px dashed #28a745;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
}
.drop-zone span {
  background-color: #28a745;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
  min-width: 40px;
  text-align: center;
}
.drop-zone span:hover {
  background-color: #1e7e34;
}

/* Compact mode styles */
.compact {
  font-size: 0.95rem !important;
}
.game-container.compact {
  max-width: 400px;
  padding: 0.5rem !important;
}
.reference-section.compact {
  padding: 2px 5px !important;
}
.reference-sentence.compact {
  font-size: 1rem !important;
}
.instruction.compact {
  font-size: 0.9rem !important;
}
.words-container.compact {
  gap: 5px !important;
  min-height: 36px !important;
  padding: 7px !important;
}
.word-item.compact,
.word-item.placed.compact {
  padding: 4px 8px !important;
  font-size: 0.95rem !important;
  min-width: 28px !important;
  border-radius: 14px !important;
}
.word-item.selected.compact {
  min-width: 28px !important;
}
.drop-zone.compact {
  min-height: 36px !important;
  padding: 7px !important;
  gap: 5px !important;
  border-radius: 4px !important;
}
.drop-zone.compact.rtl {
  direction: rtl !important;
  justify-content: center !important;
  flex-direction: row-reverse !important;
}
.drop-zone.compact.rtl .word-slot {
  direction: rtl !important;
}
.drop-zone.compact.rtl .preset-text,
.drop-zone.compact.rtl .word-item.placed,
.drop-zone.compact.rtl .placeholder {
  direction: rtl !important;
  text-align: right !important;
}
.placeholder.compact {
  padding: 4px 8px !important;
  font-size: 0.95rem !important;
  min-width: 28px !important;
  border-radius: 14px !important;
}
.preset-text.compact {
  font-size: 0.95rem !important;
  padding: 4px !important;
}
.actions.compact {
  margin: 15px 0 !important;
}
.actions.compact ion-button {
  margin: 0 4px !important;
  --padding-start: 6px !important;
  --padding-end: 6px !important;
  font-size: 0.95rem !important;
  --border-radius: 4px !important;
  --min-width: 60px !important;
}
.feedback.compact {
  font-size: 0.95rem !important;
  padding: 3px !important;
}
</style>
